'use client';

import { Drawer, Heading } from '@petsmart-ui/sparky';
import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';

type BaseDrawerProps = {
    isOpen: boolean;
    onCloseAction: () => void;
    children: ReactNode;
    title?: ReactNode;
    footer?: ReactNode;
    align?: 'right' | 'bottom';
    closeOnClickOutside?: boolean;
    testId?: string;
    ariaLabelledById?: string;
    className?: string;
    headerClassName?: string;
    bodyClassName?: string;
    footerClassName?: string;
    headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    headingIcon?: ReactNode;
};

export const BaseDrawer = (props: BaseDrawerProps) => {
    const {
        isOpen,
        onCloseAction,
        children,
        title,
        footer,
        align = 'right',
        closeOnClickOutside,
        testId,
        ariaLabelledById,
        className,
        headerClassName,
        bodyClassName,
        footerClassName,
        headingTag = 'h2',
        headingIcon,
    } = props;

    const autoHeadingId = useId();
    const labelledByID = ariaLabelledById ?? (title ? autoHeadingId : undefined);

    // Don't render drawer on server or first client render to prevent Sparky animation issues
    // This avoids scroll lock, focus trap, and aria-modal activation on initial page load
    const [hasBeenRendered, setHasBeenRendered] = useState(false); // Long live sparky :)
    useEffect(() => {
        setHasBeenRendered(true);
    }, []);

    if (!hasBeenRendered) return null;

    return (
        <Drawer
            align={align}
            isActive={isOpen}
            onClose={onCloseAction}
            data-testid={testId}
            className={className}
            ariaLabelledBy={labelledByID}
            dismissible={closeOnClickOutside}
        >
            {title && (
                <Drawer.Header className={headerClassName}>
                    {headingIcon && headingIcon}
                    <Heading id={labelledByID} tagName={headingTag} size="headline">
                        {title}
                    </Heading>
                </Drawer.Header>
            )}

            <Drawer.Body className={bodyClassName}>{children}</Drawer.Body>

            {footer && <Drawer.Footer className={footerClassName}>{footer}</Drawer.Footer>}
        </Drawer>
    );
};
